import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useApolloClient, useQuery } from '@apollo/client/react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Columns3,
  LayoutDashboard,
  Plus,
  Search,
  Sparkles,
} from 'lucide-react';
import {
  Link,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useParams,
  useResolvedPath,
  useSearchParams,
} from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  BuyerLeadDashboardRowFragment,
  BuyerLeadDnDColumnFragment,
} from '@graphql/__generated__/graphql';
import { ActionsMenu } from '@modules/_shared/components/buyer-lead/BuyerLeadActionToolbar';
import { BuyerLeadAddModal } from '@modules/_shared/components/buyer-lead/BuyerLeadAddModal';
import { BuyerLeadEditDrawer } from '@modules/_shared/components/buyer-lead/BuyerLeadEditDrawer';
import { BuyerLeadMatchingDrawer } from '@modules/_shared/components/buyer-lead/BuyerLeadMatchingDrawer';
import { getEffectiveUser } from '@modules/_shared/components/buyer-lead/buyerLeadMlsFallback';
import { GET_BUYER_LEAD_STAGES } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import { hasBuyerLeadInStage } from '@modules/_shared/components/buyer-lead/buyerLeadRefresh';
import { BuyerLeadsKanban } from '@modules/_shared/components/buyer-lead/BuyerLeadsKanban';
import {
  BUYERS_SEARCH_PARAMS_SCOPE,
  buildSearchParamsFilters,
  getBuyerLeadStageId,
  parseBuyerLeadsWhereParam,
  withCurrentBuyersWhere,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { useDropRejectedBuyersWhere } from '@modules/_shared/components/buyer-lead/useDropRejectedBuyersWhere';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { subscribeFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { ListingBuyersAutoMatching } from '@modules/listings/components/ListingBuyersAutoMatching';
import { ListingBuyersDashboard } from '@modules/listings/ListingBuyersDashboard';
import { GET_LISTING_BUYERS_DASHBOARD } from '@modules/listings/listingBuyersDashboardQueries';
import { GET_LISTING_SURROUNDING_PLACES } from '@modules/listings/lotsQueries';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { type Translate, useLocale } from '@shared/hooks/locale';

import { LISTING_BUYERS_ANCHORS } from './listingBuyersTour';

const EMAIL_CAMPAIGN_REFRESH_INTERVAL_MS = 2_500;
const EMAIL_CAMPAIGN_REFRESH_TIMEOUT_MS = 60_000;

export const GET_LISTING_BUYERS_LEAD = gql(/* GraphQL */ `
  query GetListingBuyersLead(
    $id: uuid!
    $includeBuyerLeads: Boolean!
    $buyerLeadsWhere: buyer_leads_bool_exp!
  ) {
    matched_stage: lead_stages(
      where: {
        pipeline: { name: { _eq: "house_sales" }, lead_type: { _eq: "buyer" } }
        name: { _eq: "matched" }
      }
      limit: 1
    ) {
      id
      name
      label
    }
    lots_by_pk(id: $id) {
      id
      title
      mandate_type
      lot_reference
      auto_matching_filters
      ...EmailTemplateFormListing
      ...DefaultBuyersMatchingFilters
      property {
        id
        locality
        locality_id
        postcode
        number_of_rooms
        computed_surface
        living_surface
        property_images(order_by: { order_nr: asc }, limit: 1) {
          id
          image {
            id
            url
          }
        }
        property_type {
          id
          label
          main_type
          name
        }
      }
      buyer_leads(
        where: $buyerLeadsWhere
        order_by: [{ lead_stage: { order_nr: asc } }, { updated_at: desc }]
      ) @include(if: $includeBuyerLeads) {
        ...BuyerLeadDnDColumn
      }
      all_buyer_leads: buyer_leads @include(if: $includeBuyerLeads) {
        id
        stage_id
        user_id
      }
    }
  }
`);

type BuyersView = 'dashboard' | 'kanban';
type BuyerLeadSummary =
  | BuyerLeadDashboardRowFragment
  | BuyerLeadDnDColumnFragment;

const isBuyersView = (value: string | null): value is BuyersView =>
  value === 'dashboard' || value === 'kanban';

const getLeadSearchLabel = (lead: BuyerLeadSummary, t: Translate): string => {
  const user = getEffectiveUser(lead);
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  return (
    (name !== '' ? name : null) ??
    user?.full_name ??
    user?.emails[0]?.email ??
    t('Unknown')
  );
};

const LeadSearchSuggest: React.FC<{
  leads: BuyerLeadSummary[];
  onSelect: (leadId: string) => void;
}> = ({ leads, onSelect }) => {
  const { t } = useLocale();
  const options = useMemo(
    () =>
      leads.map(lead => ({ id: lead.id, label: getLeadSearchLabel(lead, t) })),
    [leads, t],
  );

  return (
    <Autocomplete
      size="small"
      options={options}
      value={null}
      blurOnSelect
      clearOnBlur
      handleHomeEndKeys
      noOptionsText={t('No buyer leads')}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, option) => {
        if (option != null) {
          onSelect(option.id);
        }
      }}
      sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: '100%', sm: 260 } }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={t('Search buyer leads')}
          variant="outlined"
          size="small"
          sx={{ background: 'var(--mui-palette-background-paper)' }}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};

export const ListingBuyersLead: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const { t } = useLocale();
  const { lotId } = useParams();
  const navigate = useNavigate();
  const client = useApolloClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchParams = useRef(searchParams);
  const [emailedMatchedLeadIds, setEmailedMatchedLeadIds] = useState<string[]>(
    [],
  );

  const viewParam = searchParams.get('buyers_view');
  const buyersView: BuyersView = isBuyersView(viewParam)
    ? viewParam
    : 'dashboard';
  const [buyersSearchParams] = useScopedSearchParams(
    BUYERS_SEARCH_PARAMS_SCOPE,
  );
  const whereParam = buyersSearchParams.get('where');
  const buyerLeadsWhere = useMemo(
    () => parseBuyerLeadsWhereParam(whereParam),
    [whereParam],
  );
  // Relative to the current route: this screen is mounted under listings,
  // development lots and the dashboard.
  const matchingPath = useResolvedPath('matching');
  const matchingOpen = useMatch(`${matchingPath.pathname}/*`) != null;
  const includeBuyerLeads =
    buyersView === 'kanban' || matchingOpen || emailedMatchedLeadIds.length > 0;

  // `initialSearchParams` is a mount-time snapshot used to reset transient
  // filter params when opening/closing drawers. It can carry a stale
  // `buyers_view` (e.g. the tab first loaded in kanban), so always override it
  // with the currently active view to avoid flipping dashboard↔kanban.
  const restoreSearch = useMemo(() => {
    const params = withCurrentBuyersWhere(
      initialSearchParams.current,
      searchParams,
    );
    params.set('buyers_view', buyersView);
    return params.toString();
  }, [buyersView, searchParams]);

  const { data, previousData, error, loading, refetch } = useQuery(
    GET_LISTING_BUYERS_LEAD,
    {
      variables: {
        id: lotId ?? '',
        includeBuyerLeads,
        buyerLeadsWhere,
      },
      skip: lotId == null,
      pollInterval:
        emailedMatchedLeadIds.length > 0
          ? EMAIL_CAMPAIGN_REFRESH_INTERVAL_MS
          : 0,
    },
  );
  const droppingRejectedWhere = useDropRejectedBuyersWhere(error);

  const { data: stagesData } = useQuery(GET_BUYER_LEAD_STAGES);

  useEffect(() => {
    const unsubscribe = subscribeFeedRefresh(() => {
      void refetch();
    });
    return unsubscribe;
  }, [refetch]);

  const { data: suroundingPlacesData } = useQuery(
    GET_LISTING_SURROUNDING_PLACES,
    {
      variables: { id: lotId ?? '' },
      skip: lotId == null,
    },
  );

  const surroundingPlaces = useMemo(
    () =>
      suroundingPlacesData?.get_surrounding_listing_places?.map(
        ({ place_id }) => place_id,
      ) ?? [],
    [suroundingPlacesData],
  );

  const resolvedData =
    data ?? (previousData?.lots_by_pk?.id === lotId ? previousData : undefined);
  const matchedStageId = getBuyerLeadStageId(
    resolvedData?.matched_stage,
    'matched',
  );
  const listing = resolvedData?.lots_by_pk ?? null;
  const [dashboardBuyerLeads, setDashboardBuyerLeads] = useState<
    BuyerLeadDashboardRowFragment[]
  >([]);
  const kanbanBuyerLeads = useMemo(
    () => listing?.buyer_leads ?? [],
    [listing?.buyer_leads],
  );
  const allBuyerLeads = useMemo(
    () => listing?.all_buyer_leads ?? [],
    [listing?.all_buyer_leads],
  );
  const buyerLeads = useMemo(
    () => (buyersView === 'kanban' ? kanbanBuyerLeads : dashboardBuyerLeads),
    [buyersView, kanbanBuyerLeads, dashboardBuyerLeads],
  );

  useEffect(() => {
    if (
      matchedStageId == null ||
      emailedMatchedLeadIds.length === 0 ||
      allBuyerLeads.length === 0
    ) {
      return;
    }

    if (
      !hasBuyerLeadInStage(allBuyerLeads, emailedMatchedLeadIds, matchedStageId)
    ) {
      setEmailedMatchedLeadIds([]);
    }
  }, [allBuyerLeads, emailedMatchedLeadIds, matchedStageId]);

  useEffect(() => {
    if (emailedMatchedLeadIds.length === 0) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => setEmailedMatchedLeadIds([]),
      EMAIL_CAMPAIGN_REFRESH_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [emailedMatchedLeadIds]);

  const stageColumns = useMemo(
    () =>
      stagesData?.lead_stages.map(stage => ({
        id: stage.id,
        label: stage.label ?? '',
        status: stage.status,
      })) ?? [],
    [stagesData?.lead_stages],
  );

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Search is collapsed to an icon and expands inline on click, matching the
  // standard ListToolbar pattern used in the other list views.
  const [showSearch, setShowSearch] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const collapseSearchAfterBlur = useCallback(
    (searchContainer: HTMLElement) => {
      window.setTimeout(() => {
        const activeElement = document.activeElement;
        const autocompletePopper = document.querySelector(
          '.MuiAutocomplete-popper:hover',
        );
        if (
          autocompletePopper != null ||
          (activeElement instanceof HTMLElement &&
            (searchContainer.contains(activeElement) ||
              activeElement.closest('.MuiAutocomplete-popper') != null))
        ) {
          return;
        }
        setShowSearch(false);
      }, 100);
    },
    [],
  );
  useEffect(() => {
    if (!showSearch) {
      return;
    }
    const focusInput = () => {
      searchContainerRef.current
        ?.querySelector<HTMLInputElement>('input')
        ?.focus();
    };
    focusInput();
    const rafId = window.requestAnimationFrame(focusInput);
    return () => window.cancelAnimationFrame(rafId);
  }, [showSearch]);

  // Rows are always selectable; bulk actions replace the toolbar once at least
  // one buyer lead is selected, like in the kanban view.
  const selectionInProgress = selectedIds.length > 0;
  const resetSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  }, []);

  const selectedLeads = useMemo(
    () =>
      buyerLeads
        .filter(lead => selectedIds.includes(lead.id))
        .map(lead => ({
          id: lead.id,
          stage_id: lead.stage_id,
          user: getEffectiveUser(lead),
        })),
    [buyerLeads, selectedIds],
  );

  const openLead = useCallback(
    (leadId: string) => {
      navigate({
        pathname: `./${leadId}`,
        search: restoreSearch,
      });
    },
    [navigate, restoreSearch],
  );

  // Built in the parent so the toolbar CTA and the dashboard empty-state /
  // "recent matchings" actions share one destination.
  const matchingButtonTo = useMemo(() => {
    const baseParams = withCurrentBuyersWhere(
      initialSearchParams.current,
      searchParams,
    );
    const search =
      listing == null
        ? baseParams
        : buildSearchParamsFilters(listing, surroundingPlaces, baseParams);
    // Keep the user on their current view — matching is launched from both the
    // dashboard card and the kanban toolbar.
    search.set('buyers_view', buyersView);
    return { pathname: 'matching', search: search.toString() };
  }, [listing, surroundingPlaces, buyersView, searchParams]);

  // Opens the (reused) auto-matching settings drawer over the current view.
  const autoMatchingTo = useMemo(
    () => ({ pathname: 'auto-matching', search: restoreSearch }),
    [restoreSearch],
  );

  const setBuyersView = useCallback(
    (next: BuyersView) => {
      setSearchParams(
        prev => {
          const updated = new URLSearchParams(prev);
          updated.set('buyers_view', next);
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // After matching/adding, refresh both the kanban-source and dashboard queries
  // so counts and lists stay in sync regardless of the active view.
  const refetchAll = useCallback(() => {
    void refetch();
    if (lotId != null) {
      void client.query({
        query: GET_LISTING_BUYERS_DASHBOARD,
        variables: { id: lotId },
        fetchPolicy: 'network-only',
      });
    }
    return Promise.resolve();
  }, [refetch, client, lotId]);

  const matchingRoute = !readOnly ? (
    <Route
      path="matching"
      element={
        <BuyerLeadMatchingDrawer
          listing={listing?.buyer_leads == null ? null : listing}
          refetch={refetchAll}
          destinationStageId={matchedStageId}
          onClose={() => {
            navigate({
              pathname: '.',
              search: restoreSearch,
            });
          }}
        />
      }
    />
  ) : undefined;

  if (error != null && resolvedData == null && !droppingRejectedWhere) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        position: 'relative',
      }}
    >
      {error != null && resolvedData != null && !droppingRejectedWhere && (
        <Alert severity="warning" sx={{ m: 2 }}>
          {error.message}
        </Alert>
      )}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 2,
          py: 1,
          flexShrink: 0,
          borderBottom: theme =>
            `1px solid ${(theme.vars ?? theme).palette.divider}`,
        }}
      >
        <Box
          {...tourAnchor(LISTING_BUYERS_ANCHORS.view)}
          sx={{ display: 'inline-flex' }}
        >
          <SegmentedToggleButtonGroup
            value={buyersView}
            onChange={next => {
              if (isBuyersView(next)) {
                setBuyersView(next);
              }
            }}
            options={[
              {
                value: 'dashboard',
                label: <LayoutDashboard size={18} />,
                ariaLabel: t('Dashboard'),
              },
              {
                value: 'kanban',
                label: <Columns3 size={18} />,
                ariaLabel: t('Kanban'),
              },
            ]}
          />
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
        >
          {!readOnly && listing != null && selectionInProgress ? (
            <>
              <Button variant="text" size="small" onClick={resetSelection}>
                {t('Cancel')}
              </Button>
              <Suspense fallback={null}>
                <ActionsMenu
                  selectedLeads={selectedLeads}
                  columns={stageColumns}
                  onReset={resetSelection}
                  parent={listing}
                  matchedBulkEmailStageId={matchedStageId}
                  onEmailCampaignStarted={buyerLeadIds => {
                    if (buyersView !== 'kanban' || buyerLeadIds.length === 0) {
                      return;
                    }

                    setEmailedMatchedLeadIds(currentIds => [
                      ...new Set([...currentIds, ...buyerLeadIds]),
                    ]);
                  }}
                />
              </Suspense>
            </>
          ) : (
            <>
              {buyerLeads.length > 0 && (
                <ClickAwayListener onClickAway={() => setShowSearch(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {showSearch ? (
                      <Box
                        ref={searchContainerRef}
                        onBlurCapture={event =>
                          collapseSearchAfterBlur(event.currentTarget)
                        }
                        sx={{ display: 'flex' }}
                      >
                        <LeadSearchSuggest
                          leads={buyerLeads}
                          onSelect={leadId => {
                            setShowSearch(false);
                            openLead(leadId);
                          }}
                        />
                      </Box>
                    ) : (
                      <Tooltip title={t('Search buyer leads')}>
                        <IconButton
                          size="small"
                          onClick={() => setShowSearch(true)}
                        >
                          <Search size={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ClickAwayListener>
              )}
              {!readOnly && listing != null && (
                <>
                  {buyersView === 'kanban' && (
                    <Button
                      variant="outlined"
                      component={Link}
                      to={matchingButtonTo}
                      startIcon={<Sparkles size={16} />}
                    >
                      {t('Match manually')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<Plus size={16} />}
                    onClick={() => setAddModalOpen(true)}
                  >
                    {t('Add buyer lead')}
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Box>

      {listing != null &&
        (buyersView === 'kanban' ? (
          <BuyerLeadsKanban
            parent={listing}
            buyerLeads={kanbanBuyerLeads}
            offerInOverlay
            readOnly={readOnly}
            hideToolbar
            selectedItems={selectedIds}
            onSelectedItemsChange={setSelectedIds}
            extraRoutes={matchingRoute}
            refetchQueriesOnAdd={[
              {
                query: GET_LISTING_BUYERS_LEAD,
                variables: { id: lotId, includeBuyerLeads, buyerLeadsWhere },
              },
              {
                query: GET_LISTING_BUYERS_DASHBOARD,
                variables: { id: lotId },
              },
            ]}
          />
        ) : (
          <>
            {lotId != null && (
              <ListingBuyersDashboard
                lotId={lotId}
                readOnly={readOnly}
                matchingButtonTo={matchingButtonTo}
                autoMatchingTo={autoMatchingTo}
                onOpenAddModal={() => setAddModalOpen(true)}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onSetSelectedIds={setSelectedIds}
                onBuyerLeadsChange={setDashboardBuyerLeads}
                parent={listing}
                matchedStageId={matchedStageId}
              />
            )}
            <Routes>
              {matchingRoute}
              {!readOnly && lotId != null && (
                <Route
                  path="auto-matching/*"
                  element={
                    <Drawer
                      title={t('Buyers auto-matching')}
                      maxWidth={900}
                      onClose={() => {
                        navigate({
                          pathname: '.',
                          search: restoreSearch,
                        });
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <ListingBuyersAutoMatching
                          lotId={lotId}
                          readOnly={readOnly}
                        />
                      </Box>
                    </Drawer>
                  }
                />
              )}
              <Route
                path=":buyerLeadId"
                element={
                  <BuyerLeadEditDrawer
                    offerInOverlay
                    readOnly={readOnly}
                    onClose={() => {
                      navigate({
                        pathname: '.',
                        search: restoreSearch,
                      });
                    }}
                  />
                }
              />
            </Routes>
          </>
        ))}

      {!readOnly && lotId != null && (
        <BuyerLeadAddModal
          lotId={lotId}
          stages={stagesData?.lead_stages}
          defaultStageName="interested"
          opened={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          refetchQueries={[
            {
              query: GET_LISTING_BUYERS_LEAD,
              variables: {
                id: lotId,
                includeBuyerLeads: true,
                buyerLeadsWhere,
              },
            },
            { query: GET_LISTING_BUYERS_DASHBOARD, variables: { id: lotId } },
          ]}
        />
      )}
    </Box>
  );
};
